/**
 * Design System Tokens - Central Export
 *
 * This module exports all design tokens for the EroChat design system.
 * Import from this file to access all token types and default values.
 *
 * @example
 * ```typescript
 * import {
 *   defaultSpacing,
 *   defaultRadius,
 *   defaultColors,
 *   type DesignTokens
 * } from '@/design-system/tokens';
 * ```
 */

// =============================================================================
// TYPE EXPORTS
// =============================================================================

export type {
  // Main interfaces
  DesignTokens,
  DesignPreset,
  PresetMetadata,
  PresetCategory,

  // Color tokens
  ColorTokens,
  BrandColorTokens,
  ButtonColorTokens,
  TextColorTokens,
  SurfaceColorTokens,
  StatusColorTokens,
  StatusColorSet,
  RarityColorTokens,
  RarityColorSet,
  RarityTier,
  ChartColorTokens,
  SwipeColorTokens,
  NavigationColorTokens,
  GlassColorTokens,
  ActionColorTokens,
  ActionColorSet,
  OnlineStatusColorTokens,
  MessageColorTokens,

  // Spacing tokens
  SpacingTokens,
  SpacingKey,

  // Radius tokens
  RadiusTokens,
  RadiusScaleKey,
  RadiusAliasKey,
  RadiusKey,

  // Shadow tokens
  ShadowTokens,
  ShadowKey,

  // Typography tokens
  TypographyTokens,
  FontFamilyTokens,
  FontSizeTokens,
  FontWeightTokens,
  LineHeightTokens,
  LetterSpacingTokens,
  SemanticTypographyTokens,
  SemanticTypographyToken,
  FontSizeKey,
  FontWeightKey,
  LineHeightKey,
  LetterSpacingKey,

  // Animation tokens
  AnimationTokens,
  AnimationDurationKey,
  AnimationEasingKey,
  AnimationScaleKey,

  // Z-Index tokens
  ZIndexTokens,
  ZIndexKey,

  // Component tokens
  ComponentTokens,
  ButtonComponentTokens,
  InputComponentTokens,
  CardComponentTokens,
  NavigationComponentTokens,

  // Utility types
  PartialDesignTokens,
  TokenPath,
  CSSVariableName,
  CSSVariableMap,
} from './types';

// =============================================================================
// DEFAULT VALUE EXPORTS
// =============================================================================

// Spacing
export { defaultSpacing, spacingPx, spacingKeys, getSpacingCSSVariables } from './spacing';

// Radius
export {
  defaultRadius,
  radiusPx,
  radiusScaleKeys,
  radiusAliasKeys,
  radiusKeys,
  defaultRadiusAliasMapping,
  getRadiusCSSVariables,
} from './radius';

// Shadows
export { defaultShadows, getShadowVar, shadowsToCssVars } from './shadows';

// Animation
export { defaultAnimation, transitions, getAnimationVar, animationToCssVars } from './animation';

// Z-Index
export { defaultZIndex, zIndexStyles, getZIndexVar, zIndexToCssVars } from './z-index';

// Typography
export {
  defaultFontFamilies,
  defaultFontSizes,
  defaultFontWeights,
  defaultLineHeights,
  defaultLetterSpacings,
  defaultSemanticTypography,
  defaultTypographyTokens,
  createLegacyTypography,
  generateTypographyCSSVariables,
} from './typography';

// Colors
export {
  defaultBrandColors,
  defaultButtonColors,
  defaultTextColors,
  defaultSurfaceColors,
  defaultStatusColors,
  defaultRarityColors,
  defaultChartColors,
  defaultSwipeColors,
  defaultNavigationColors,
  defaultGlassColors,
  defaultActionColors,
  defaultOnlineStatusColors,
  defaultMessageColors,
  defaultColorTokens as defaultColors,
  createLegacyColorPalette,
  generateColorCSSVariables,
  getRarityColor,
  getStatusColor,
  getChartColor,
} from './colors';

// Branding
export { defaultBrandingTokens, type BrandingTokens } from './branding';

// Extended Component Tokens
export type {
  ButtonSizeTokens,
  ButtonVariantTokens,
  ButtonComponentTokensExtended,
  InputSizeTokens,
  InputStateTokens,
  InputComponentTokensExtended,
  CardVariantTokens,
  CardComponentTokensExtended,
  NavigationComponentTokensExtended,
  ModalSizeTokens,
  ModalComponentTokensExtended,
  AvatarSizeTokens,
  AvatarComponentTokensExtended,
  BadgeSizeTokens,
  BadgeComponentTokensExtended,
  AllComponentTokensExtended,
} from './components';

export {
  defaultButtonTokens,
  defaultInputTokens,
  defaultCardTokens,
  defaultNavigationTokens,
  defaultModalTokens,
  defaultAvatarTokens,
  defaultBadgeTokens,
  defaultExtendedComponentTokens,
  generateButtonCSSVariables,
  generateInputCSSVariables,
  generateAllComponentCSSVariables,
} from './components';

// =============================================================================
// COMBINED DEFAULT TOKENS
// =============================================================================

import { defaultSpacing, getSpacingCSSVariables } from './spacing';
import { defaultRadius, getRadiusCSSVariables } from './radius';
import { defaultShadows, shadowsToCssVars } from './shadows';
import { defaultAnimation, animationToCssVars } from './animation';
import { defaultZIndex, zIndexToCssVars } from './z-index';
import {
  fontFamilies as defaultFontFamily,
  fontSizes as defaultFontSize,
  fontWeights as defaultFontWeight,
  lineHeights as defaultLineHeight,
  letterSpacings as defaultLetterSpacing,
  semanticTypography as defaultSemanticTypography,
  generateTypographyCSSVariables,
} from './typography';
import { defaultColorTokens as defaultColors, generateColorCSSVariables } from './colors';
import type { DesignTokens, ComponentTokens } from './types';

/**
 * Default component tokens
 */
export const defaultComponentTokens: ComponentTokens = {
  button: {
    heightSm: '2.75rem', // 44px
    heightMd: '3rem', // 48px
    heightLg: '3.25rem', // 52px
    paddingX: defaultSpacing.md,
    fontWeight: defaultFontWeight.semibold,
  },
  input: {
    height: '2.75rem', // 44px
    paddingX: defaultSpacing.md,
    borderWidth: '1px',
  },
  card: {
    padding: defaultSpacing.lg,
    gap: defaultSpacing.md,
  },
  navigation: {
    heightDesktop: '4rem', // 64px
    heightMobile: '4rem', // 64px
  },
};

/**
 * Complete default design tokens
 *
 * This object contains all default token values that can be used
 * as a base for creating custom presets.
 */
export const defaultDesignTokens: DesignTokens = {
  colors: defaultColors,
  spacing: defaultSpacing,
  radius: defaultRadius,
  shadows: defaultShadows,
  typography: {
    fontFamily: defaultFontFamily,
    fontSize: defaultFontSize,
    fontWeight: defaultFontWeight,
    lineHeight: defaultLineHeight,
    letterSpacing: defaultLetterSpacing,
    semantic: defaultSemanticTypography,
  },
  animation: defaultAnimation,
  zIndex: defaultZIndex,
  components: defaultComponentTokens,
};

/**
 * Generate all CSS variables from design tokens
 */
export function generateAllCSSVariables(tokens: DesignTokens = defaultDesignTokens): Record<string, string> {
  return {
    ...getSpacingCSSVariables(tokens.spacing),
    ...getRadiusCSSVariables(tokens.radius),
    ...shadowsToCssVars(tokens.shadows),
    ...animationToCssVars(tokens.animation),
    ...zIndexToCssVars(tokens.zIndex),
    ...generateTypographyCSSVariables(tokens.typography),
    ...generateColorCSSVariables(tokens.colors),
    // Component tokens
    '--component-button-height-sm': tokens.components.button.heightSm,
    '--component-button-height-md': tokens.components.button.heightMd,
    '--component-button-height-lg': tokens.components.button.heightLg,
    '--component-button-padding-x': tokens.components.button.paddingX,
    '--component-button-font-weight': String(tokens.components.button.fontWeight),
    '--component-input-height': tokens.components.input.height,
    '--component-input-padding-x': tokens.components.input.paddingX,
    '--component-input-border-width': tokens.components.input.borderWidth,
    '--component-card-padding': tokens.components.card.padding,
    '--component-card-gap': tokens.components.card.gap,
    '--nav-height-desktop': tokens.components.navigation.heightDesktop,
    '--nav-height-mobile': tokens.components.navigation.heightMobile,
  };
}

/**
 * Apply design tokens to the DOM as CSS custom properties
 */
export function applyTokensToDOM(tokens: DesignTokens = defaultDesignTokens): void {
  const cssVars = generateAllCSSVariables(tokens);
  const root = document.documentElement;

  Object.entries(cssVars).forEach(([property, value]) => {
    root.style.setProperty(property, value);
  });
}
